// La configuración de la tanda (ADR-2026-10-30-videos-cortos-con-el-agente), plegada como
// `ConfiguracionDelCatalogo`: la voz del lote (abre el selector) y subtítulos (sólo con voz). La
// indicación general no tiene campo: la guarda el asistente cuando la persona pide algo para todos
// los videos (ADR-2026-10-31-videos-cortos-sin-campo-de-indicacion). A la derecha, "Escribir N guiones" cuando hay
// pendientes (sin guion, o con voz y sin locución).
import { ChevronDown, Loader2, Mic, Settings2, VolumeX, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Interruptor } from '../../../config_sections/Interruptor';
import { TituloEditable } from '../catalogoAgente/ConfiguracionDelCatalogo';
import { costoDeLaTanda, type PlanDeVideos } from './plan';

export { TituloEditable };

export interface ConfiguracionDeLaTandaProps {
  plan: PlanDeVideos;
  nombreDeVoz: string;
  escribiendo: boolean;
  pendientesDeEscritura: number;
  onElegirVoz: () => void;
  onSubtitulos: (s: boolean) => void;
  onEscribir: () => void;
}

export const ConfiguracionDeLaTanda: React.FC<ConfiguracionDeLaTandaProps> = ({ plan, nombreDeVoz, escribiendo, pendientesDeEscritura, onElegirVoz, onSubtitulos, onEscribir }) => {
  const [abierta, setAbierta] = useState(false);
  const sinVoz = plan.voz === null;

  return (
    <section className="rounded-[20px] bg-[#F5F6FA] p-3">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta} className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-0.5 text-left">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-brand-black shadow-sm">
            <Settings2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-[14.5px] font-semibold text-brand-black">Configuración del lote</span>
            <span className="block truncate text-[12.5px] text-[#3C3C43]/55">
              Voz: {nombreDeVoz} · {plan.productos.length} video{plan.productos.length === 1 ? '' : 's'} · {costoDeLaTanda(plan)} créditos en total
            </span>
          </span>
          <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.5)] transition-transform ${abierta ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" />
        </button>
        {pendientesDeEscritura > 0 && (
          <button
            type="button"
            onClick={onEscribir}
            disabled={escribiendo}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.095)] py-1.5 px-3 text-[13px] font-semibold text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.165)] disabled:opacity-60"
          >
            {escribiendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Wand2 className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />}
            {escribiendo ? 'Escribiendo…' : `Escribir ${pendientesDeEscritura} guion${pendientesDeEscritura === 1 ? '' : 'es'}`}
          </button>
        )}
      </div>

      {abierta && (
        <div className="animate-fade-in mt-3 flex flex-col gap-3 rounded-lg bg-white p-3">
          <div className="flex items-center gap-3">
            <span className="w-[110px] shrink-0 text-[13px] font-semibold text-[#3C3C43]/60">Voz del lote</span>
            <button type="button" onClick={onElegirVoz} className="inline-flex min-w-0 items-center gap-2 rounded-md border border-black/[0.1] bg-white py-2 px-3 text-left text-[14px] font-medium text-brand-black transition-colors hover:bg-black/[0.02]">
              {sinVoz ? <VolumeX className="h-4 w-4 shrink-0 text-[#3C3C43]/50" aria-hidden="true" /> : <Mic className="h-4 w-4 shrink-0 text-brand-primary" aria-hidden="true" />}
              <span className="truncate">{nombreDeVoz}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.4)]" aria-hidden="true" />
            </button>
            <span className="hidden text-[12.5px] text-[#3C3C43]/50 sm:block">Vale para los videos sin voz propia; cada tarjeta puede tener la suya.</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-[110px] shrink-0 text-[13px] font-semibold text-[#3C3C43]/60">Subtítulos</span>
            <Interruptor etiqueta="Subtítulos" activo={!sinVoz && plan.subtitulos} disabled={sinVoz} onChange={onSubtitulos} />
            <span className="text-[12.5px] text-[#3C3C43]/50">{sinVoz ? 'Necesitan la voz en off' : 'Siguen la voz, palabra por palabra'}</span>
          </div>
        </div>
      )}
    </section>
  );
};
